import React, { useState } from 'react';
import TreeView, { flattenTree } from 'react-accessible-treeview';
import { Icon } from '@iq/react-components';
import './TreeView.scss';

const FoldableTreeView = ({
  data,
  expandedIds = [],
  selectedIds = {}, // Keep as an object, we'll convert it before passing to TreeView
  treeType = '',
  setMachExpandedIds = () => {},
  setMimsExpandedIds = () => {},
  setEcExpandedIds = () => {},
  onNodeSelect = () => {}, // Callback for node selection
}) => {
  const treeData = flattenTree(data);

  // Create a map from string ID to node for quick lookup
  const idToNode = React.useMemo(() => {
    const map = {};
    treeData.forEach((node) => {
      map[node.id] = node;
    });
    return map;
  }, [treeData]);

  // Helper function to get all ancestor IDs for a given ID
  const getAncestorIds = React.useCallback(
    (nodeId) => {
      const ancestors = [];
      let currentNode = idToNode[nodeId];

      // Walk up the parent chain
      while (currentNode) {
        ancestors.push(currentNode.id);
        if (currentNode.parent === null || currentNode.parent === undefined) break;
        currentNode = idToNode[currentNode.parent];
      }

      return ancestors;
    },
    [idToNode]
  );

  // Create an array of valid IDs that exist in this tree
  const validIds = React.useMemo(() => {
    return treeData.map((node) => node.id);
  }, [treeData]);

  // Keep track of manually expanded IDs
  const [manuallyExpandedIds, setManuallyExpandedIds] = useState([]);

  // Get all IDs that need to be expanded (target + ancestors)
  const mergedExpandedIds = React.useMemo(() => {
    const allIds = new Set();

    expandedIds.forEach((id) => {
      // Skip non-string IDs (like initial `1`)
      if (typeof id !== 'string') return;

      // Check if this ID exists in the tree
      if (!idToNode[id]) return;

      // Get the target node and all its ancestors
      const ancestorIds = getAncestorIds(id);
      ancestorIds.forEach((ancestorId) => allIds.add(ancestorId));
    });

    // Merge with manually expanded
    manuallyExpandedIds.forEach((id) => allIds.add(id));

    return Array.from(allIds);
  }, [expandedIds, manuallyExpandedIds, getAncestorIds, idToNode, treeType]);

  const handleClickLink = (e, element, handleSelect) => {
    e.stopPropagation();
    handleSelect(e);

    // The element.id is already the string ID
    const nodeId = element.id;

    let mappingData = {};

    if (treeType === 'mach') {
      mappingData = {
        machId: nodeId,
        mimsId: element.metadata?.mimsId || null,
        ecId: element.metadata?.ecId || null,
      };

      // Handle tree expansion for MACH tree
      if (element.metadata?.expandMims) {
        // Important: APPEND to existing IDs rather than replacing them
        if (Array.isArray(element.metadata.expandMach)) {
          const validMachExpand = element.metadata.expandMach.filter((id) => validIds.includes(id));
          setMachExpandedIds((prevIds) => {
            // Create a set from existing and new IDs to avoid duplicates
            const uniqueIds = new Set([...prevIds, ...validMachExpand]);
            return Array.from(uniqueIds);
          });
        }

        if (Array.isArray(element.metadata.expandMims)) {
          setMimsExpandedIds((prevIds) => {
            const uniqueIds = new Set([...prevIds, ...element.metadata.expandMims]);
            return Array.from(uniqueIds);
          });
        }

        if (Array.isArray(element.metadata.expandEc)) {
          setEcExpandedIds((prevIds) => {
            const uniqueIds = new Set([...prevIds, ...element.metadata.expandEc]);
            return Array.from(uniqueIds);
          });
        }
      }
    } else if (treeType === 'mims') {
      mappingData = {
        mimsId: nodeId,
      };
    } else if (treeType === 'ec') {
      mappingData = {
        ecId: nodeId,
      };
    }

    // Always call onNodeSelect no matter which tree was clicked
    if (typeof onNodeSelect === 'function') {
      onNodeSelect(mappingData);
    }
  };

  const updateManuallyExpandedIds = (isCurrentlyExpanded, nodeId) => {
    if (!isCurrentlyExpanded) {
      // Node is being expanded
      setManuallyExpandedIds((prev) => [...prev, nodeId]);
    } else {
      // Node is being collapsed
      setManuallyExpandedIds((prev) => prev.filter((id) => id !== nodeId));
    }
  };

  // Selected IDs are already strings - just filter to valid ones
  const selectedIdsList = React.useMemo(() => {
    const selectedStringIds = Object.keys(selectedIds);
    return selectedStringIds.filter((id) => validIds.includes(id));
  }, [selectedIds, validIds]);

  return (
    <TreeView
      multiSelect={true}
      data={treeData}
      className="foldable-tree-view"
      expandedIds={mergedExpandedIds}
      selectedIds={selectedIdsList}
      nodeRenderer={({
        element,
        getNodeProps,
        level,
        isBranch,
        isExpanded,
        isSelected,
        handleExpand,
        handleSelect,
      }) => {
        return (
          <div
            {...getNodeProps({ onClick: () => {} })}
            style={{ paddingLeft: 20 * (level - 1) }}
            onClick={(e) => handleClickLink(e, element, handleSelect)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleClickLink(e, element, handleSelect);
              }
            }}
            tabIndex={0}
            className={`tree-node ${isSelected ? 'selected-node' : ''}`}
          >
            {isBranch ? (
              <ArrowIcon
                isOpen={isExpanded}
                onClick={(e) => {
                  // Important: Only handle expansion without affecting selection
                  handleExpand(e);

                  // Extracted function called to manage expanded IDs
                  updateManuallyExpandedIds(isExpanded, element.id);
                }}
              />
            ) : (
              <span className="node-spacer"></span>
            )}
            <span className={`name ${isSelected ? 'selected-text' : ''}`}>{element.name}</span>
          </div>
        );
      }}
    />
  );
};

const ArrowIcon = ({ isOpen, onClick }) => {
  const position = isOpen ? 'down' : 'forwards';
  return (
    <span className="arrow-container">
      <Icon
        icon={`he-${position}`}
        size="xs"
        className="arrow"
        onClick={(e) => {
          e.stopPropagation();
          onClick(e);
        }}
      />
    </span>
  );
};

export default FoldableTreeView;
